// ─── Status Page — the merge-field catalogue ─────────────────────────
//
// ONE definition, both ends: the API renders with it and the editor's toolbar
// offers it. A token that is not in this list cannot be saved, and every token
// in this list is guaranteed to resolve — the render model is built from the
// same keys (services/status/message-templates.ts), so "offered" and "resolves"
// are the same set by construction rather than by two lists agreeing today.
//
// ── WHY THE DIALECT IS DELIBERATELY POOR ──
// Operator source is rendered by nunjucks, and nunjucks looks a filter up AT
// RENDER TIME and THROWS if it does not exist. During a fan-out that means the
// batch dies — so a template saved with `{{ x | title }}` would be accepted,
// look fine in preview, and then take out an outage notification months later.
// So: NO FILTERS, ever. Every token below is already finished — localised,
// formatted, and joined — which is the same rule services/status/templates.ts
// states for itself ("the templates take LABELS, never enums"), pushed one level
// out to the people writing the copy.
//
// This is also why PCP's OTHER merge library cannot be reused here. The
// Smartguides catalogue (apps/web/src/lib/templateVariables.ts) is LiquidJS —
// its entries carry `| default: '—'` and `regexReplace`, which are a nunjucks
// PARSE ERROR and an unknown-filter THROW respectively. Same idea, incompatible
// engine; only the picker mechanism is shared.
/** Which message a template overrides. */
export const STATUS_MESSAGE_EVENTS = ['incident.new', 'incident.update', 'incident.resolved'];
export const STATUS_MESSAGE_EVENT_LABELS_DA = {
    'incident.new': 'Ny hændelse',
    'incident.update': 'Opdatering',
    'incident.resolved': 'Løst',
};
export const STATUS_MESSAGE_EVENT_LABELS_EN = {
    'incident.new': 'New incident',
    'incident.update': 'Update',
    'incident.resolved': 'Resolved',
};
export const isStatusMessageEvent = (s) => typeof s === 'string' && STATUS_MESSAGE_EVENTS.includes(s);
/**
 * Every token an incident message can carry.
 *
 * Derived from IncidentNotifyContext — if a field is not on that interface it is
 * not here, because it would render empty. `componentList` is the pre-joined
 * form of the array: joining is exactly the kind of thing an operator would
 * otherwise need a filter for.
 *
 * There is deliberately NO audience token (f-02002). An audience is how WE
 * target a message, not information for the customer — printing "Gælder for:
 * C&B Hosted" only left recipients wondering what it meant. A saved template
 * that still says `{{ audienceList }}` renders it as nothing.
 */
export const STATUS_MERGE_TOKENS = [
    { key: 'incidentTitle', labelDa: 'Titel', labelEn: 'Title', sample: 'Driftsforstyrrelse e-nettet', sms: true },
    { key: 'incidentRefId', labelDa: 'Reference', labelEn: 'Reference', sample: 'i-00042', sms: true },
    { key: 'kindLabel', labelDa: 'Type', labelEn: 'Type', sample: 'Hændelse', sms: true },
    { key: 'statusLabel', labelDa: 'Status', labelEn: 'Status', sample: 'Undersøges', sms: true },
    { key: 'impactLabel', labelDa: 'Påvirkning', labelEn: 'Impact', sample: 'Nedsat ydeevne', sms: true },
    { key: 'updateBody', labelDa: 'Beskeden', labelEn: 'Message', sample: 'Vi undersøger sagen.', sms: true },
    { key: 'componentList', labelDa: 'Berørte systemer', labelEn: 'Affected systems', sample: 'Classic, Mæglerunivers', sms: true },
    { key: 'occurredAtText', labelDa: 'Tidspunkt', labelEn: 'Time', sample: '7. august 2026 kl. 15.07', sms: false },
    { key: 'publicUrl', labelDa: 'Link til hændelsen', labelEn: 'Incident link', sample: 'https://status.realequity.ai/incident/i-00042', sms: true },
];
const TOKEN_KEYS = new Set(STATUS_MERGE_TOKENS.map((t) => t.key));
/**
 * Check operator source BEFORE it can be saved.
 *
 * Rejecting rather than stripping, deliberately: the server has no DOM and no
 * sanitiser, so quietly removing something would leave the operator believing
 * they had written a message they had not. A refusal at save time is a
 * conversation; a silent edit is a surprise during an outage.
 *
 * What it enforces:
 *   • every `{{ … }}` names a token from the catalogue above;
 *   • NO filters — `{{ x | y }}` is refused, because nunjucks resolves a filter
 *     at RENDER and throws, i.e. mid-fan-out, months later;
 *   • the only statement allowed is `{% if %}` / `{% else %}` / `{% endif %}`.
 *     Loops and includes have nothing to iterate here and `{% include %}` would
 *     reach the filesystem.
 */
export function validateTemplateSource(source, opts = {}) {
    const text = String(source ?? '');
    const problems = [];
    if (!text.trim())
        return [{ code: 'empty', detail: 'The message is empty' }];
    for (const m of text.matchAll(/\{\{([\s\S]*?)\}\}/g)) {
        const inner = m[1].trim();
        if (inner.includes('|')) {
            problems.push({
                code: 'filter',
                detail: `Filters are not supported: {{ ${inner} }}. Every field is already formatted.`,
            });
            continue;
        }
        // A bare token, nothing else — no expressions, no lookups we cannot promise.
        const key = inner.split(/\s+/)[0];
        if (!TOKEN_KEYS.has(key)) {
            problems.push({ code: 'unknown-token', detail: `Unknown field: {{ ${inner} }}` });
            continue;
        }
        if (opts.sms && !STATUS_MERGE_TOKENS.find((t) => t.key === key)?.sms) {
            problems.push({ code: 'unknown-token', detail: `{{ ${key} }} is not available in a text message` });
        }
    }
    for (const m of text.matchAll(/\{%([\s\S]*?)%\}/g)) {
        const inner = m[1].trim();
        const head = inner.split(/\s+/)[0];
        if (!['if', 'else', 'elif', 'endif'].includes(head)) {
            problems.push({ code: 'tag', detail: `Only {% if %} is supported here, not {% ${head} %}` });
        }
    }
    return problems;
}
/** A sample model for the preview, keyed exactly like the render model — so a
 *  preview that looks right cannot be a template that renders blank. */
export function sampleMergeModel() {
    const out = {};
    for (const t of STATUS_MERGE_TOKENS)
        out[t.key] = t.sample;
    return out;
}
// ─── The wire contract ───────────────────────────────────────────────
//
// ONE definition per message, imported by the route AND the editor. The public
// status API's shapes were declared twice and drifted five times in one session
// (f-01560); this is the same lesson applied before it can happen again.
/** The two locales a subscriber can hold (status_subscribers.locale). */
export const STATUS_MESSAGE_LOCALES = ['da', 'en'];
export const coerceStatusMessageLocale = (l) => (l === 'en' ? 'en' : 'da');
/** Narrow an unknown jsonb column to a usable pin set. Returns null for anything
 *  that is not a well-formed snapshot, so a hand-edited row degrades to the
 *  built-in rather than throwing inside a fan-out. */
export function coerceTemplateSnapshot(value) {
    if (!value || typeof value !== 'object')
        return null;
    const raw = value;
    if (!isStatusMessageEvent(raw.event))
        return null;
    if (!raw.byLocale || typeof raw.byLocale !== 'object')
        return null;
    const byLocale = {};
    for (const locale of STATUS_MESSAGE_LOCALES) {
        const pin = raw.byLocale[locale];
        if (!pin || typeof pin !== 'object')
            continue;
        const p = pin;
        if (typeof p.body !== 'string' || !p.body)
            continue;
        byLocale[locale] = {
            id: typeof p.id === 'string' ? p.id : '',
            subject: typeof p.subject === 'string' ? p.subject : '',
            body: p.body,
        };
    }
    return { event: raw.event, byLocale };
}
//# sourceMappingURL=status-merge.js.map